import { useEffect, useState } from "react";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";
// import { ErrorModal } from "../../../main/components/ErrorModal";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
} from "@mui/material";

import { DialogHeader } from "../../../auth/components/PasswordRecoveryModal";
import * as yup from "yup";
import TextInput from "../../../main/components/TextInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  SelectInputMultiple,
  type Option,
} from "../../../main/components/SelectInputMultiple";
import { clientsApi, usersApi, usinasApi } from "../../../services/api";
import type { NewCollaboratorForm } from "../types";
import { phoneRegex, validateCPF } from "../../../main/utils/utils";
import type { UsinaByClient } from "../../usinas/types";
import CpfInput from "../../../main/components/CpfInput";
import PhoneNumberInput from "../../../main/components/PhoneNumberInput";
import { Role } from "../../users/types";

const schema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  email: yup.string().required("Email é obrigatório"),
  phone: yup.string().matches(phoneRegex, "Formato de Telefone inválido"),
  cpf: yup
    .string()
    .test("isCpfValid", "O CPF informado é inválido", (v) =>
      !!v ? validateCPF(v) : true
    ),
  usinasIds: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
    )
    .min(1, "Selecione pelo menos uma usina")
    .required("Usina é obrigatório"),
  roles: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string().required(),
        label: yup.string().required(),
      })
    )
    .min(1, "Selecione pelo menos um perfil")
    .required("Perfil é obrigatório"),
});

export type NewCollaboratorClient = yup.InferType<typeof schema>;

export function AddCollaboratorFormModal(props: {
  showAddFormModal: boolean;
  setShowAddFormModal: (value: boolean) => void;
  itemText: string;
  itemName: string;
  onMount: () => void;
  clientId: string;
}) {
  const {
    showAddFormModal,
    setShowAddFormModal,
    itemText,
    itemName,
    onMount,
    clientId,
  } = props;
  const [showAddItemConfirmationModal, setShowAddItemConfirmationModal] =
    useState<boolean>(false);
  const onClose = () => setShowAddFormModal(false);
  const [showAddItemSuccesModal, setShowAddItemSuccessModal] =
    useState<boolean>(false);
  const [showAddItemErrorModal, setShowAddItemErrorModal] =
    useState<boolean>(false);
  const [usinasOptions, setUsinasOptions] = useState<Option[]>([]);
  const [allRoles, setAllRoles] = useState<Option[]>([]);
  const onOpen = async () => {
    try {
      const usinasResponse = await usinasApi.getUsinasByClientId(clientId);
      setUsinasOptions(
        usinasResponse.data.map((usina: UsinaByClient) => ({
          value: usina.usinaIds.id,
          label: usina.name || usina.usinaIds.id,
        }))
      );
      const rolesResponse = await usersApi.getAllRoles();

      const parsedRoles: Option[] = rolesResponse.map((role: Role) => ({
        value: role.id,
        label: role.name,
      }));

      setAllRoles(parsedRoles);
    } catch (error) {
      console.error("Erro ao carregar usinas", error);
    }
  };

  const onConfirm = async (data: NewCollaboratorClient) => {
    const postData: NewCollaboratorForm = {
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      cpf: data.cpf || null,
      usinasIds: data.usinasIds.map((usina) => usina.value),
      roles: data.roles.map((role) => role.label),
    };

    try {
      await clientsApi.AddCollaborator(clientId, postData);
      setShowAddFormModal(false);
      schema.__default;
      onMount();
    } catch (error) {
      setShowAddItemErrorModal(true);
    }
  };

  const onSubmit = () => {
    setShowAddItemConfirmationModal(true);
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!showAddFormModal) {
      return;
    }
    onOpen();
  }, [showAddFormModal, clientId]);
  const handleCpfFieldBlur = async () => {
    try {
      await trigger("cpf");
    } catch (error) {}
  };
  const handlePhoneNumberFieldBlur = async () => {
    try {
      await trigger("phone");
    } catch (error) {}
  };
  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
        open={showAddFormModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogHeader id="customized-dialog-title" onClose={onClose}>
          {`Adicionar ${itemName} `}
        </DialogHeader>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          dividers
        >
          <Grid onSubmit={handleSubmit(onSubmit)} component={"form"}>
            <Grid item xs={12}>
              <TextInput
                control={control}
                fieldName={`name`}
                label="Nome"
                errors={errors}
                sx={{
                  width: "100%",
                  marginBottom: "10px",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                control={control}
                fieldName="email"
                label="Email"
                errors={errors}
                sx={{
                  width: "100%",
                  marginBottom: "10px",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <PhoneNumberInput
                control={control}
                fieldName={`phone`}
                label="Telefone"
                errors={errors}
                sx={{
                  width: "100%",
                  marginBottom: "10px",
                }}
                onBlur={() => handlePhoneNumberFieldBlur()}
              />
            </Grid>
            <Grid item xs={12}>
              <CpfInput
                control={control}
                fieldName="cpf"
                label="CPF"
                errors={errors}
                sx={{
                  width: "100%",
                  marginBottom: "10px",
                }}
                onBlur={() => handleCpfFieldBlur()}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectInputMultiple
                control={control}
                fieldName="usinasIds"
                label="Usinas do Colaborador"
                errors={errors}
                sx={{
                  marginBottom: "10px",
                }}
                options={usinasOptions}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectInputMultiple
                control={control}
                fieldName="roles"
                label="Perfis do Colaborador"
                errors={errors}
                sx={{
                  marginBottom: "10px",
                }}
                options={allRoles}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <DialogActions>
              <Button color="secondary" onClick={onClose}>
                {"Cancelar"}
              </Button>
              <Button variant="contained" color={"primary"} type="submit">
                {"Confirmar"}
              </Button>
            </DialogActions>
          </Grid>
        </DialogContent>
      </Dialog>
      <ConfirmationModal
        contentText={`Deseja realmente adicionar?`}
        open={showAddItemConfirmationModal}
        onConfirm={handleSubmit(onConfirm)}
        setOpen={setShowAddItemConfirmationModal}
        title={`Adicionar ${itemText}`}
        setOpenErrorModal={setShowAddItemErrorModal}
      />
      <ActionAlert
        message={`Adição do ${itemText} realizada com sucesso`}
        severity="success"
        open={showAddItemSuccesModal}
        setOpen={setShowAddItemSuccessModal}
        onClose={() => {
          setShowAddFormModal(false);
          onMount();
        }}
      />
      <ActionAlert
        message={`Ocorreu um erro ao adicionar ${itemText} ao usuário`}
        severity="error"
        open={showAddItemErrorModal}
        setOpen={setShowAddItemErrorModal}
        onClose={() => {
          setShowAddFormModal(false);
        }}
      />
      {/* <ErrorModal
        contentText={`Ocorreu um erro ao adicionar ${itemText} ao usuário`}
        open={showAddItemErrorModal}
        setOpen={setShowAddItemErrorModal}
        title={`Erro ao adicionar`}
        onClose={() => {
          setShowAddFormModal(false);
        }}
      />
      <SuccessModal
        contentText={`Adição d${itemText} realizada com sucesso`}
        open={showAddItemSuccesModal}
        setOpen={setShowAddItemSuccessModal}
        title={`Adição realizada`}
        onClose={() => {
          setShowAddFormModal(false);
          onMount();
        }}
      /> */}
    </>
  );
}
